import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const de = {
  valueMissing: {
    name: "Bitte gib deinen Namen an.",
    email: "Bitte gib deine E-Mail-Adresse an.",
    message: "Beschreib kurz, worum es geht.",
    consent: "Bitte bestätige die Datenschutzerklärung.",
  },
  typeMismatchEmail: "Bitte gib eine gültige E-Mail-Adresse an.",
};

const invalidMsg = (el, field) => {
  if (el.validity.valueMissing) return de.valueMissing[field] || "Bitte ausfüllen.";
  if (el.validity.typeMismatch && field === "email") return de.typeMismatchEmail;
  return "";
};

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", website: "" });
  const tsRef = useRef(Date.now());
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const [fader, setFader] = useState("");

  const loadChallenge = () => {
    fetch(`${API}/captcha/challenge`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setCaptcha(d || false); setFader(""); })
      .catch(() => setCaptcha(false));
  };

  useEffect(() => { loadChallenge(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onInvalid = (field) => (e) => e.target.setCustomValidity(invalidMsg(e.target, field));
  const onInput = (e) => e.target.setCustomValidity("");

  const submit = async (e) => {
    e.preventDefault();
    if (sending || !captcha) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, ts: tsRef.current, captcha_id: captcha.cid, captcha_answer: Number(fader) }),
      });
      if (res.status === 429) {
        toast.error("Zu viele Anfragen. Bitte versuch es in ein paar Minuten erneut.");
        return;
      }
      if (res.status === 400) {
        const d = await res.json().catch(() => null);
        toast.error(d?.detail || "Spam-Schutz nicht bestanden. Bitte versuch es erneut.");
        loadChallenge();
        return;
      }
      if (!res.ok) throw new Error("send failed");
      setSent(true);
      toast.success("Nachricht gesendet. Wir melden uns.");
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte versuch es erneut oder schreib an info@dimedial.de.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div data-testid="contact-form-success" className="border-t hairline pt-10">
        <p className="font-display text-3xl md:text-4xl uppercase text-paper tracking-tight">Angekommen.</p>
        <p className="mt-4 text-grey leading-relaxed max-w-md">
          Danke für deine Nachricht. Wir melden uns in der Regel innerhalb eines Werktages.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
      <div>
        <label htmlFor="cf-name" className="label-text block mb-1">Name</label>
        <input
          id="cf-name"
          data-testid="contact-form-name"
          type="text"
          required
          maxLength={120}
          value={form.name}
          onChange={set("name")}
          onInvalid={onInvalid("name")}
          onInput={onInput}
          placeholder="Dein Name"
          className="field-input"
          autoComplete="name"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
        <div>
          <label htmlFor="cf-email" className="label-text block mb-1">E-Mail</label>
          <input
            id="cf-email"
            data-testid="contact-form-email"
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            onInvalid={onInvalid("email")}
            onInput={onInput}
            placeholder="name@firma.de"
            className="field-input"
            autoComplete="email"
          />
        </div>
        <div>
          <label htmlFor="cf-phone" className="label-text block mb-1">Handynummer <span className="normal-case tracking-normal text-grey/60">(optional)</span></label>
          <input
            id="cf-phone"
            data-testid="contact-form-phone"
            type="tel"
            maxLength={60}
            value={form.phone}
            onChange={set("phone")}
            placeholder="+49 …"
            className="field-input"
            autoComplete="tel"
          />
        </div>
      </div>
      <div>
        <label htmlFor="cf-message" className="label-text block mb-1">Worum geht es?</label>
        <textarea
          id="cf-message"
          data-testid="contact-form-message"
          required
          maxLength={4000}
          rows={4}
          value={form.message}
          onChange={set("message")}
          onInvalid={onInvalid("message")}
          onInput={onInput}
          placeholder="Ein Film, eine Stimme, eine Kampagne …"
          className="field-input resize-none"
        />
      </div>
      <div data-testid="contact-form-captcha">
        <span className="label-text block mb-1">Spam-Schutz</span>
        {captcha ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-base text-ink" data-testid="contact-form-captcha-question">Wie viel ist <strong>{captcha.question}</strong>?</span>
            <input
              type="number"
              inputMode="numeric"
              value={fader}
              onChange={(e) => setFader(e.target.value)}
              className="w-24 rounded-xl border border-black/15 px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-black/20"
              placeholder="Ergebnis"
              data-testid="contact-form-fader"
              aria-label="Ergebnis der Rechenaufgabe"
            />
          </div>
        ) : (
          <p className="text-sm text-grey" data-testid="contact-form-captcha-loading">Spam-Schutz wird geladen …</p>
        )}
      </div>
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px", width: "1px", height: "1px", overflow: "hidden" }} aria-hidden="true">
        <label htmlFor="cf-website">Website (bitte frei lassen)</label>
        <input
          id="cf-website"
          data-testid="contact-form-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={form.website}
          onChange={set("website")}
        />
      </div>
      <label className="flex items-start gap-4 cursor-pointer group" data-testid="contact-form-consent-label">
        <input
          type="checkbox"
          required
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          onInvalid={onInvalid("consent")}
          onInput={onInput}
          data-testid="contact-form-consent"
          className="mt-1 h-4 w-4 shrink-0 appearance-none border border-[rgba(245,245,240,0.35)] bg-transparent checked:bg-gold checked:border-gold transition-colors duration-300 cursor-pointer"
        />
        <span className="text-sm text-grey leading-relaxed group-hover:text-paper transition-colors duration-500">
          Ich habe die{" "}
          <Link to="/datenschutz" data-testid="contact-form-datenschutz-link" className="text-paper underline decoration-gold/60 underline-offset-4 hover:text-gold transition-colors duration-500">
            Datenschutzerklärung
          </Link>{" "}
          gelesen und bin einverstanden, dass meine Angaben zur Beantwortung der Anfrage verwendet werden.
        </span>
      </label>
      <button
        type="submit"
        data-testid="contact-form-submit"
        disabled={sending || !captcha}
        className="link-arrow disabled:opacity-50 disabled:pointer-events-none"
      >
        {sending ? "Wird gesendet …" : "Absenden"}
        <ArrowRight size={18} className="arrow" />
      </button>
    </form>
  );
};

export default ContactForm;
